import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useEffect, useRef, useState } from 'react';
import { useVsjFlow } from './vsj-flow';
import { READING } from './vsj-reading-timeline';

const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY as string | undefined) ?? '';
const BASEMAP_URL  = `https://api.maptiler.com/maps/dataviz-dark/style.json?key=${MAPTILER_KEY}`;

const CUADRA_COLOR = '#3FD8EA';
const ENV_COLOR    = '#2FA4B5';
const MAX_ENV_STREETS = 6;

const ZOOM_LOCATE  = 16;
const ZOOM_READING = 15.35;

interface StreetLine {
  coords: [number, number][];
  distPx: number;
}

/**
 * Pull real street geometries from the rendered basemap around a point.
 * This is what lets the reading follow the shape of the actual streets
 * (00A: "la forma de la expansión es la forma de la red") without any backend.
 */
function collectStreets(map: maplibregl.Map, lngLat: [number, number]): StreetLine[] {
  const c = map.project(lngLat);
  const R = 150;
  const feats = map.queryRenderedFeatures([
    [c.x - R, c.y - R],
    [c.x + R, c.y + R],
  ]);

  const lines: StreetLine[] = [];
  const seen = new Set<string>();

  for (const f of feats) {
    if (f.layer?.type !== 'line') continue;
    const tag = `${f.layer.id} ${f.sourceLayer ?? ''}`;
    if (!/road|street|transport|highway|bridge|tunnel|path|minor|major|motorway/i.test(tag)) continue;

    let coords: [number, number][] | null = null;
    if (f.geometry.type === 'LineString') {
      coords = f.geometry.coordinates as [number, number][];
    } else if (f.geometry.type === 'MultiLineString') {
      coords = (f.geometry.coordinates as [number, number][][])[0] ?? null;
    }
    if (!coords || coords.length < 2) continue;

    const key = `${coords[0]}|${coords[coords.length - 1]}|${coords.length}`;
    if (seen.has(key)) continue;
    seen.add(key);

    let d = Infinity;
    for (const co of coords) {
      const p = map.project(co as [number, number]);
      const dd = (p.x - c.x) ** 2 + (p.y - c.y) ** 2;
      if (dd < d) d = dd;
    }
    lines.push({ coords, distPx: Math.sqrt(d) });
  }

  lines.sort((a, b) => a.distPx - b.distPx);
  return lines.slice(0, MAX_ENV_STREETS + 1);
}

function setInteractive(map: maplibregl.Map, on: boolean) {
  const handlers = [
    map.dragPan, map.scrollZoom, map.doubleClickZoom,
    map.touchZoomRotate, map.keyboard, map.boxZoom,
  ];
  handlers.forEach((h) => (on ? h.enable() : h.disable()));
}

/** Quiet the basemap so it reads as territory, not as cartographic software. */
function calmBasemap(map: maplibregl.Map) {
  try {
    for (const layer of map.getStyle().layers ?? []) {
      if (layer.type !== 'symbol') continue;
      if (/poi|housenumber|airport/i.test(layer.id)) {
        map.setLayoutProperty(layer.id, 'visibility', 'none');
        continue;
      }
      try { map.setPaintProperty(layer.id, 'text-opacity', 0.6); } catch { /* no text */ }
      try { map.setPaintProperty(layer.id, 'icon-opacity', 0.45); } catch { /* no icon */ }
    }
  } catch { /* best-effort styling */ }
}

export function VsjMapStage(): JSX.Element {
  const { place, envPhase, envRead, locateResult } = useVsjFlow();
  const confirmedGeometry = locateResult?.confirmedGeometry ?? null;

  const containerRef      = useRef<HTMLDivElement>(null);
  const mapRef            = useRef<maplibregl.Map | null>(null);
  const markerElRef       = useRef<HTMLDivElement | null>(null);
  const markerRef         = useRef<maplibregl.Marker | null>(null);
  const layerIdsRef       = useRef<string[]>([]);
  const timersRef         = useRef<number[]>([]);
  const ranRef            = useRef(false);
  const edgeLayerRef      = useRef(false); // true when vsj-confirmed-edge-* layers are live

  const [dim, setDim] = useState<'none' | 'strong' | 'soft'>('none');

  // ── Init (once) ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAP_URL,
      center: [place.lng, place.lat],
      zoom: envRead ? ZOOM_READING : ZOOM_LOCATE,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
    });
    map.touchZoomRotate.disableRotation();
    map.on('load', () => calmBasemap(map));

    // Custom marker: quiet dot with a slow breathing ring — no default teardrop.
    const el = document.createElement('div');
    el.className = 'vsj-marker';
    el.innerHTML = '<span class="vsj-marker-ring"></span><span class="vsj-marker-dot"></span>';
    markerElRef.current = el;
    markerRef.current = new maplibregl.Marker({ element: el })
      .setLngLat([place.lng, place.lat])
      .addTo(map);

    mapRef.current = map;
    return () => {
      timersRef.current.forEach(clearTimeout);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      markerElRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Place changes ───────────────────────────────────────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markerRef.current?.setLngLat([place.lng, place.lat]);
    map.easeTo({ center: [place.lng, place.lat], duration: 500 });
  }, [place.lat, place.lng]);

  // ── Reading choreography ────────────────────────────────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const t0 = performance.now();
    const later = (fn: () => void, ms: number) => {
      timersRef.current.push(window.setTimeout(fn, ms));
    };
    /** Schedule relative to ceremony start, tolerant of late tile loading. */
    const at = (ceremonyMs: number, fn: () => void) => {
      later(fn, Math.max(0, ceremonyMs - (performance.now() - t0)));
    };

    const setMarkerQuiet = (quiet: boolean) => {
      markerElRef.current?.classList.toggle('vsj-marker--quiet', quiet);
    };

    const clearHighlights = () => {
      for (const id of layerIdsRef.current) {
        if (map.getLayer(id + '-glow')) map.removeLayer(id + '-glow');
        if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(id)) map.removeSource(id);
      }
      layerIdsRef.current = [];
    };

    const showStreets = (mode: 'ceremony' | 'restore') => {
      const lines = collectStreets(map, [place.lng, place.lat]);
      lines.forEach((ln, i) => {
        const id = `vsj-street-${i}`;
        if (map.getSource(id)) return;
        const isCuadra = i === 0;

        const fade = mode === 'ceremony'
          ? (isCuadra ? READING.cuadraFade : READING.streetFade)
          : 350;

        map.addSource(id, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: ln.coords },
            properties: {},
          },
        });

        if (isCuadra) {
          map.addLayer({
            id: id + '-glow', type: 'line', source: id,
            paint: { 'line-color': CUADRA_COLOR, 'line-width': 9, 'line-blur': 6, 'line-opacity': 0 },
          });
          // Transition props are runtime-valid but missing from the paint typings.
          map.setPaintProperty(id + '-glow', 'line-opacity-transition' as never, { duration: fade + 200 } as never);
        }
        map.addLayer({
          id, type: 'line', source: id,
          paint: {
            'line-color': isCuadra ? CUADRA_COLOR : ENV_COLOR,
            'line-width': isCuadra ? 3 : 2,
            'line-opacity': 0,
          },
        });
        map.setPaintProperty(id, 'line-opacity-transition' as never, { duration: fade } as never);
        map.setPaintProperty(id, 'line-width-transition' as never, { duration: fade } as never);
        layerIdsRef.current.push(id);

        const reveal = () => {
          if (!map.getLayer(id)) return;
          map.setPaintProperty(id, 'line-opacity', isCuadra ? 0.9 : 0.4);
          if (isCuadra) {
            map.setPaintProperty(id, 'line-width', 4);
            map.setPaintProperty(id + '-glow', 'line-opacity', 0.28);
          }
        };

        if (mode === 'ceremony') {
          const when = isCuadra
            ? READING.cuadraAt
            : READING.streetDelays[Math.min(i - 1, READING.streetDelays.length - 1)];
          at(when, reveal);
        } else {
          later(reveal, i * 90);
        }
      });
    };

    const whenTilesReady = (fn: () => void) => {
      let done = false;
      const run = () => { if (!done) { done = true; fn(); } };
      if (map.isStyleLoaded() && map.areTilesLoaded()) {
        later(run, 250);
      } else {
        map.once('idle', run);
        later(run, 1100); // safety: never stall the moment on slow tiles
      }
    };

    if (envPhase === 'reading') {
      ranRef.current = true;
      setDim('strong');
      setMarkerQuiet(true); // the block's light takes over; the ring rests
      setInteractive(map, false);
      // One continuous, sub-perceptual drift outward across the whole ceremony.
      map.easeTo({
        center: [place.lng, place.lat],
        zoom: ZOOM_READING,
        duration: READING.zoomMs,
        easing: (t) => t * t * (3 - 2 * t),
      });
      whenTilesReady(() => showStreets('ceremony'));
    } else if (envPhase === 'read') {
      setDim('soft');
      setMarkerQuiet(true);
      setInteractive(map, true);
      // The city makes room for the reading: the active place rises above the
      // panel instead of hiding behind it.
      const h = map.getContainer().clientHeight;
      map.easeTo({ padding: { top: 0, left: 0, right: 0, bottom: Math.round(h * 0.42) }, duration: 700 });
      if (!ranRef.current) {
        // Arrived via back-navigation: restore the read state without ceremony.
        ranRef.current = true;
        whenTilesReady(() => showStreets('restore'));
      }
    } else {
      setDim('none');
      setMarkerQuiet(false);
      setInteractive(map, true);
      clearHighlights();
      ranRef.current = false;
      map.easeTo({
        center: [place.lng, place.lat],
        zoom: ZOOM_LOCATE,
        padding: { top: 0, left: 0, right: 0, bottom: 0 },
        duration: 600,
      });
    }

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envPhase]);

  // ── Confirmed edge illumination (location step only) ────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    const removeEdge = () => {
      try { if (map.getLayer('vsj-confirmed-edge-glow')) map.removeLayer('vsj-confirmed-edge-glow'); } catch { /* ok */ }
      try { if (map.getLayer('vsj-confirmed-edge-line')) map.removeLayer('vsj-confirmed-edge-line'); } catch { /* ok */ }
      try { if (map.getSource('vsj-confirmed-edge'))     map.removeSource('vsj-confirmed-edge'); }     catch { /* ok */ }
      edgeLayerRef.current = false;
    };

    if (envPhase !== 'idle' || !confirmedGeometry) {
      removeEdge();
      return;
    }

    if (edgeLayerRef.current) return; // already showing this geometry

    try {
      map.addSource('vsj-confirmed-edge', {
        type: 'geojson',
        data: { type: 'Feature', geometry: confirmedGeometry as never, properties: {} },
      });
      map.addLayer({
        id: 'vsj-confirmed-edge-glow', type: 'line', source: 'vsj-confirmed-edge',
        paint: { 'line-color': CUADRA_COLOR, 'line-width': 9, 'line-blur': 6, 'line-opacity': 0 },
      });
      map.addLayer({
        id: 'vsj-confirmed-edge-line', type: 'line', source: 'vsj-confirmed-edge',
        paint: { 'line-color': CUADRA_COLOR, 'line-width': 3, 'line-opacity': 0 },
      });
      map.setPaintProperty('vsj-confirmed-edge-line', 'line-opacity-transition' as never, { duration: 600 } as never);
      map.setPaintProperty('vsj-confirmed-edge-glow', 'line-opacity-transition' as never, { duration: 800 } as never);
      window.setTimeout(() => {
        try {
          map.setPaintProperty('vsj-confirmed-edge-line', 'line-opacity', 0.9);
          map.setPaintProperty('vsj-confirmed-edge-glow', 'line-opacity', 0.28);
        } catch { /* layer may have been removed before the timeout fired */ }
      }, 50);
      edgeLayerRef.current = true;
    } catch { /* guard against double-add in React StrictMode dev cycle */ }

    return removeEdge;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedGeometry, envPhase]);

  return (
    <div className="vsj-map-stage">
      <div className="vsj-map-canvas" ref={containerRef} />
      <div className="vsj-map-blend" />
      <div className={`vsj-map-dim vsj-map-dim--${dim}`} />
    </div>
  );
}
